/**
 * The slice of `@amplitude/analytics-browser` the exit flush needs. Structural so
 * `shared/` stays free of the SDK dependency — pass the SDK module itself.
 */
export interface ExitFlushClient {
  setTransport: (transport: 'beacon' | 'fetch') => void;
  flush: () => { promise: Promise<void> };
}

let pending = 0;

/**
 * How long to wait for a flush to settle before forcing the transport back to
 * `fetch` anyway. The SDK's `flush()` resolves in every reachable case — including
 * the skip case, where an interval flush is already in flight — so a real flush
 * settles in milliseconds. This only fires on the pathological never-settling
 * promise, and it exists so one wedged flush cannot leave the app on beacon for
 * the rest of the session (losing response codes and retries on in-flow events).
 */
export const FLUSH_SETTLE_TIMEOUT_MS = 5000;

/**
 * Drain the Amplitude queue over `navigator.sendBeacon`, which the browser keeps
 * sending after the document unloads; the SDK's default `fetch` transport is
 * cancelled with the page.
 *
 * The SDK reads the transport only when the batch is sent, several awaits into
 * `flush()`, so `fetch` is restored in the flush continuation — restoring it
 * synchronously would hand the exit batch back to fetch. The count keeps an
 * earlier exit flush from restoring fetch under a later one still in flight.
 *
 * A flush that never settles is bounded by `FLUSH_SETTLE_TIMEOUT_MS`: whichever
 * of the promise settling or the timeout fires first restores the transport, and
 * the other is a no-op, so the count stays balanced and the app is never left on
 * beacon.
 *
 * Call right after the exit event's `track()`, with no await between them: once
 * the SDK is initialised, `track` enqueues before it returns and `flush()` drains
 * that queue. Never await this.
 */
export function flushOnExit(client: ExitFlushClient, timeoutMs: number = FLUSH_SETTLE_TIMEOUT_MS) {
  client.setTransport('beacon');
  pending += 1;

  let settled = false;
  const restore = () => {
    if (settled) return;
    settled = true;
    pending -= 1;
    if (pending > 0) {
      return;
    }
    client.setTransport('fetch');
  };

  void client.flush().promise.then(restore, restore);
  // A wedged flush must not leave the app on beacon forever. The timer and the
  // promise race; the first to run wins and the other is a no-op via `settled`.
  setTimeout(restore, timeoutMs);
}
